/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import PropTypes from 'prop-types';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import './PointOfSale.css';
import MDSnackbar from 'components/MDSnackbar';

const initAttr = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  customer_id: '',
  quotation_id: '',
  vat_id: '2',
  status: 'edit',
  branch_receive: '',
  credit_day: '',
  credit_money: '',
  print_url: '',
  print_no: '0',
  detail_no: '0',
  created_at: '',
  updated_at: '',
  print: '',
  quotation: '',
  payment_type: 'CASH',
  shipping_id: '',
  time_shipping: '00:00',
  car_id: '',
  brandsend_id: '',
  printso_no: '',
  address: '',
  address2: '',
  printinvoice_no: '',
  discount_unit: 'Y',
  date_record: Func.dateNow(),
  date_due: Func.dateNow(),
  date_shipping: Func.dateNow(),
};

const initSum = {
  count_all: 0,
  first_bill: '0',
  last_bill: '0',
  discount_coupon: 0,
  discount_approve: 0,
  discount_return: 0,
  discount_promotion: 0,
  discount_all: 0,
  total: 0,
  nettotal: 0,
  money_cash: 0,
  money_transfer: 0,
  credit_visa: 0,
  credit_mastercard: 0,
  credit_alipay: 0,
  credit_wechat: 0,
  money_send: 0,
};

const PosPrintCloseSend = forwardRef(({ parent }, ref) => {
  const mod = 'pos_closeshift';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [sum, setSum] = useState(initSum);
  const [transfer, setTransfer] = useState([]);
  const [credit, setCredit] = useState([]);
  const [cancels, setCancel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const [notify, setNotify] = useState(null);

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    },
  });

  const print = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/printsum`, app.token, {
      lang: app.lang,
      parent_id: parent.id,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setList(result.list);
          setSum(result.summary);
          setTransfer(result.transfer_list);
          setCredit(result.credit_list);
          handlePrint();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderHeader = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td align='center'>
              {attr.image === '' ? null : <img src={attr.image} alt='' width='130' />}
            </td>
          </tr>
          <tr>
            <td align='center'>{attr.branch_name}</td>
          </tr>
          <tr>
            <td align='center'>
              <div className='title'>ใบรายงานยอดนำส่ง</div>
            </td>
          </tr>
          <tr>
            <td>
              <p>&nbsp;</p>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderNumber = (title, value, line = false) => {
    return (
      <tr>
        <td>{title}</td>
        <td xs={6} align='right' className={line ? 'underline' : ''}>
          {Func.numberDisplay(value, 2)}
        </td>
      </tr>
    );
  };

  const renderCancel = () => {
    return cancels.map((row) => (
      <tr>
        <td>
          {row.code} {row.date_create}
        </td>
        <td>{Func.numberDisplay(row.nettotal, 2)}</td>
      </tr>
    ));
  };

  const renderSlip = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td colSpan={2}>รายการขาย</td>
          </tr>
          <tr>
            <td colSpan={2}>
              บิลแรก {sum.first_bill}, {Func.dateTimeDisplay(attr.first_date)}
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              สุดท้าย {sum.last_bill}, {Func.dateTimeDisplay(attr.last_date)}
            </td>
          </tr>
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
          <tr>
            <td colSpan={2}>* * บิลที่ปิดเรียบร้อย</td>
          </tr>
          <tr>
            <td>จำนวนบิล</td>
            <td align='right'>{Func.numberDisplay(sum.count_all)}</td>
          </tr>
          {renderNumber('ยอดขายก่อนลด', sum.total)}
          {renderNumber('@ลดตามจำนวน', sum.discount, true)}
          {renderNumber('หลังลดตามจำนวน', 0)}
          {renderNumber('แลกซื้อเมื่อจำนวนครบ', 0, true)}
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
          {renderNumber('หลังลดการตลาด', 0, true)}
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
          {renderNumber('ยอดรวม', sum.nettotal)}
          {renderNumber('ยอดขายไม่มีภพ.', 0)}
          {renderNumber('ยอดขายมีภพ.', 0)}
          {renderNumber('ยอดภพ.', 0, true)}
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
          <tr>
            <td colSpan={2}>รายการนำส่ง</td>
          </tr>
          {renderNumber('เงินสด', sum.total_cash)}
          {transfer && transfer.map((row) => renderNumber(row.name, row.total_count))}
          {credit && credit.map((row) => renderNumber(row.name, row.total_count))}
          {/* {renderNumber('Master Card', sum.credit_mastercard)}
          {renderNumber('VISA Card', sum.credit_visa)}
          {renderNumber('ALIPAY', sum.credit_alipay)}
          {renderNumber('WECHAT', sum.credit_wechat)}
          {renderNumber('JCB', sum.credit_jcb)}
          {renderNumber('Union Pay', sum.credit_upay)}
          {renderNumber('THAI QR', sum.money_transfer)}
          {renderNumber('ยอดชำระ', sum.nettotal)} */}
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
          <tr>
            <td colSpan={2}>รายการเงินสด</td>
          </tr>
          {renderNumber('เงินสด', sum.total_cash)}
          {renderNumber('นำส่งเงิน', attr.total_cash)}
          {renderNumber('รับเงินเพื่อทอน', attr.money_receive)}
          {renderNumber('ยอดต้องนำส่ง', attr.money_balance, true)}
          <tr>
            <td colSpan={2}>* * บิลที่มีรายการยกเลิกหลังรวมเงิน</td>
          </tr>
          {renderCancel()}
          <tr>
            <td colSpan={2}>* * บิลที่มีรายการยกเลิกระหว่างขาย</td>
          </tr>
          {renderCancel()}
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderOverShort = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td
              align='left'
              colSpan={2}
              style={{
                height: '20px',
              }}
            >
              Over / Short:
            </td>
            <td align='right'>
              ........................................................................
            </td>
          </tr>
          <tr>
            <td
              align='left'
              colSpan={2}
              style={{
                height: '20px',
              }}
            >
              Signature:
            </td>
            <td align='right'>
              ........................................................................
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderFooter = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td colSpan={2}>Cashier : {attr.user_name}</td>
          </tr>
          <tr>
            <td>{attr.code}</td>
            <td>{Func.dateTimeDisplay(attr.created_at)}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: 'none' }}>
        <div ref={printRef} className='slip'>
          {renderHeader()}
          {renderSlip()}
          {renderOverShort()}
          {renderFooter()}
          <br />.
        </div>
      </div>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      print,
    };
  });

  return (
    <div>
      {renderLoading()}
      {renderPrint()}
      {notify}
    </div>
  );
});

PosPrintCloseSend.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default PosPrintCloseSend;
